import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import BidStatusBadge from './BidStatusBadge';
import { bidService } from '../../../services/bidService';
import { getBidContractors } from '../../../services/contractorVendorLinkService';
import { parseLocalDate } from '../../../utils/dateUtils';

const BidDetailModal = ({ isOpen, onClose, bid, onConvert, onEdit, onDelete, onArchived }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [contractors, setContractors] = useState({ subContractors: [], vendors: [] });
  const [loadingContractors, setLoadingContractors] = useState(false);

  useEffect(() => {
    if (isOpen && bid?.id) {
      setLoadingContractors(true);
      getBidContractors(bid?.id)?.then(data => setContractors(data))?.catch(err => console.error('Error loading bid contractors:', err?.message))?.finally(() => setLoadingContractors(false));
    }
  }, [isOpen, bid?.id]);

  if (!isOpen || !bid) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return parseLocalDate(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(bid?.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting bid:', error);
      alert('Failed to delete bid. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const hasContractors = contractors?.subContractors?.length > 0 || contractors?.vendors?.length > 0;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold text-foreground data-text">
              {bid?.bidNumber}
            </h2>
            <BidStatusBadge status={bid?.status} />
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Customer
                </h3>
                <p className="text-base text-foreground">{bid?.customerName}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Project Name
                </h3>
                <p className="text-base text-foreground">{bid?.projectName}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Created Date
                </h3>
                <p className="text-base text-foreground">{formatDate(bid?.createdDate)}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Project Location
                </h3>
                <p className="text-base text-foreground">{bid?.projectLocation}</p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">
                Financial Breakdown
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Labor Amount</span>
                  <span className="text-base font-semibold text-foreground data-text">
                    {formatCurrency(bid?.laborAmount)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Equipment Rental</span>
                  <span className="text-base font-semibold text-foreground data-text">
                    {formatCurrency(bid?.equipmentRental)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Supplies & Materials</span>
                  <span className="text-base font-semibold text-foreground data-text">
                    {formatCurrency(bid?.suppliesMaterials)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 bg-accent/10 rounded-lg px-4 mt-4">
                  <span className="text-base font-semibold text-foreground">Total Project Amount</span>
                  <span className="text-xl font-bold text-accent data-text">
                    {formatCurrency(bid?.totalAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Contractors & Vendors Section */}
            {(hasContractors || loadingContractors) && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 border-b border-border">
                  <Icon name="HardHat" size={14} className="text-purple-500" />
                  <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                    Contractors &amp; Vendors
                  </h3>
                </div>
                {loadingContractors ? (
                  <div className="flex items-center gap-2 p-4 text-muted-foreground text-sm">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {contractors?.subContractors?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Sub-Contractors</p>
                        <div className="space-y-1">
                          {contractors?.subContractors?.map(c => (
                            <div key={c?.id} className="text-sm text-foreground">{c?.name}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {contractors?.vendors?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Vendors</p>
                        <div className="space-y-1">
                          {contractors?.vendors?.map(v => (
                            <div key={v?.id} className="text-sm text-foreground">{v?.name}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Description
              </h3>
              <p className="text-base text-foreground leading-relaxed">{bid?.description}</p>
            </div>

            {bid?.documentationLink && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Documentation
                </h3>
                <a
                  href={bid?.documentationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                >
                  <Icon name="ExternalLink" size={16} />
                  <span>View Documentation</span>
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-6 border-t border-border">
          <div className="flex items-center gap-2">
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteConfirm(true)}
              iconName="Trash2"
              iconPosition="left"
              iconSize={16}
            >
              Delete Bid
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                setArchiving(true);
                try {
                  const { error } = await bidService?.archive(bid?.id, bid?.status);
                  if (!error) {
                    onArchived && onArchived(bid);
                    onClose();
                  }
                } catch (err) {
                  console.error('Archive error:', err?.message);
                } finally {
                  setArchiving(false);
                }
              }}
              disabled={archiving}
              iconName="Archive"
              iconPosition="left"
              iconSize={16}
              className="text-muted-foreground hover:text-foreground"
            >
              {archiving ? 'Archiving...' : 'Archive'}
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                onEdit(bid);
                onClose();
              }}
              iconName="Pencil"
              iconPosition="left"
              iconSize={16}
            >
              Edit Bid
            </Button>
            {bid?.status === 'Accepted' && (
              <Button
                variant="default"
                onClick={() => {
                  onConvert(bid);
                  onClose();
                }}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={16}
              >
                Convert to Project
              </Button>
            )}
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-xl shadow-lg w-full max-w-md p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-destructive/10 rounded-full">
                <Icon name="AlertTriangle" size={24} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Delete Bid?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete bid <strong>{bid?.bidNumber}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={isDeleting}
                iconName={isDeleting ? "Loader2" : "Trash2"}
                iconPosition="left"
                iconSize={16}
              >
                {isDeleting ? 'Deleting...' : 'Delete Bid'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidDetailModal;